import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { FaTrash, FaEnvelope, FaEnvelopeOpen, FaHome } from 'react-icons/fa';
import { adminApi } from '../../services/api';
import AdminLayout from './AdminLayout';
import './Admin.css';

const AdminMessages = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [filter, setFilter] = useState('all'); // all | unread | read
  const [deleteId, setDeleteId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminApi.getAllMessages();
      const sorted = (res.data?.data || []).sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
      );
      setMessages(sorted);
    } catch (_) {
      toast.error('Error al cargar mensajes');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleMarkRead = async (id) => {
    try {
      await adminApi.markAsRead(id);
      setMessages(prev => prev.map(m => m.id === id ? { ...m, read: true } : m));
    } catch (_) {
      toast.error('Error al marcar como leído');
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await adminApi.deleteMessage(deleteId);
      setMessages(prev => prev.filter(m => m.id !== deleteId));
      setDeleteId(null);
      if (expanded === deleteId) setExpanded(null);
      toast.success('Mensaje eliminado');
    } catch (_) {
      toast.error('Error al eliminar mensaje');
    }
  };

  const handleExpand = async (m) => {
    if (expanded === m.id) { setExpanded(null); return; }
    setExpanded(m.id);
    if (!m.read) await handleMarkRead(m.id);
  };

  const filtered = messages.filter(m => {
    if (filter === 'unread') return !m.read;
    if (filter === 'read') return m.read;
    return true;
  });

  const unreadCount = messages.filter(m => !m.read).length;

  const formatDate = (iso) => {
    if (!iso) return '';
    return new Date(iso).toLocaleString('es-SV', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  return (
    <AdminLayout title="Mensajes">
      <div className="admin-page-header">
        <div className="filter-tabs">
          <button
            className={`filter-tab ${filter === 'all' ? 'active' : ''}`}
            onClick={() => setFilter('all')}
          >
            Todos ({messages.length})
          </button>
          <button
            className={`filter-tab ${filter === 'unread' ? 'active' : ''}`}
            onClick={() => setFilter('unread')}
          >
            Sin leer ({unreadCount})
          </button>
          <button
            className={`filter-tab ${filter === 'read' ? 'active' : ''}`}
            onClick={() => setFilter('read')}
          >
            Leídos ({messages.length - unreadCount})
          </button>
        </div>
      </div>

      {loading ? (
        <div className="admin-loading"><div className="spinner" /></div>
      ) : filtered.length === 0 ? (
        <div className="admin-empty-state">
          <FaEnvelope />
          <p>No hay mensajes {filter !== 'all' ? 'en esta categoría' : 'aún'}</p>
        </div>
      ) : (
        <div className="messages-list">
          {filtered.map(m => (
            <div
              key={m.id}
              className={`message-item ${!m.read ? 'message-item--unread' : ''} ${expanded === m.id ? 'message-item--expanded' : ''}`}
            >
              <div className="message-header" onClick={() => handleExpand(m)}>
                <div className="message-icon">
                  {m.read ? <FaEnvelopeOpen /> : <FaEnvelope />}
                </div>
                <div className="message-meta">
                  <div className="message-sender">
                    <strong>{m.name}</strong>
                    <span>{m.email}</span>
                    {m.phone && <span>{m.phone}</span>}
                  </div>
                  <div className="message-subject">{m.subject}</div>
                </div>
                <div className="message-right">
                  <span className="message-date">{formatDate(m.createdAt)}</span>
                  {!m.read && <span className="unread-dot" />}
                </div>
              </div>

              {expanded === m.id && (
                <div className="message-body">
                  {m.propertyTitle && (
                    <div className="message-property-ref">
                      <FaHome /> Propiedad: <strong>{m.propertyTitle}</strong>
                    </div>
                  )}
                  <div className="message-text">{m.message}</div>
                  <div className="message-actions">
                    <a
                      href={`mailto:${m.email}?subject=Re: ${m.subject}`}
                      className="btn btn--navy"
                    >
                      <FaEnvelope /> Responder por email
                    </a>
                    {m.phone && (
                      <a
                        href={`https://wa.me/${m.phone.replace(/\D/g, '')}?text=Hola%20${encodeURIComponent(m.name)}!`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn--whatsapp"
                      >
                        WhatsApp
                      </a>
                    )}
                    {!m.read && (
                      <button
                        className="btn btn--outline-navy"
                        onClick={() => handleMarkRead(m.id)}
                      >
                        <FaEnvelopeOpen /> Marcar leído
                      </button>
                    )}
                    <button
                      className="btn btn--danger-outline"
                      onClick={() => setDeleteId(m.id)}
                    >
                      <FaTrash /> Eliminar
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Confirm delete */}
      {deleteId && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>¿Eliminar mensaje?</h3>
            <p>Esta acción no se puede deshacer.</p>
            <div className="modal-actions">
              <button className="btn btn--outline-navy" onClick={() => setDeleteId(null)}>
                Cancelar
              </button>
              <button className="btn btn--danger" onClick={handleDelete}>
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminMessages;
